import { Injectable, NgZone } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http'; // New HTTP client (Angular 4.3+)

import { MechanicsService } from "../services/mechanics.service"
import { UtilsService } from "../services/utils.service"

import { MedicineSet } from "../interfaces/medicineSet.interface"
import { decrypt } from '../utils';
import { lastValueFrom } from 'rxjs';

import { environment } from "../../environments/environment"
import { UserService } from './user.service';

@Injectable()
export class SearchService {

	public config: Object = {
		maxAggressiveness: 2 // Disabling raising search aggressiveness, because anyway now we're filtering out all results that do not contain exactly all the words we're looking for
	}

	/*public searchedFields: Array<string> = [
	  "title",
	  "abstract"
	]*/

	// public familiesResults: Array<Family> = [];
	public displayedResults: Array<MedicineSet> = [];
	public allInns: any[] = []
	public searchTerms: string = ""
	public selectedCountries: any[] = []
	public totalResultsCount: number = 0

	public disclaimer: string = ""

	// Attempt to fix race condition with the search results
	private lastSearchId: number = -1;

	constructor(public ms: MechanicsService,
		private us: UserService,
		public utils: UtilsService,
		public http: HttpClient,
		private ngZone: NgZone) {

		this.getDisclaimerInResults()

	}

	async getDisclaimerInResults(): Promise<void> {

		// console.log(`search service getDisclaimer()`)

		let res

		try {
			res = await lastValueFrom(this.http.get(`/assets/documents/disclaimerInResults.html`, { responseType: 'text' }));
			this.disclaimer = res["_body"] || res;
			// console.log(`Search service - disclaimer is now = `, this.disclaimer)
		} catch (err) {
			console.error(err)
		}
	}

	search(searchTerms = this.searchTerms): void {

		const l = `ss.search() - `

		this.ms.condensedSearchContainer = false

		this.searchTerms = searchTerms || ""

		let query = {
			searchTerms: this.searchTerms.trim(),
			searchId: Math.floor(Math.random() * 10000000000)// Generate a random number to identify the search request
		}

		if (this.selectedCountries.length) {
			query["cc"] = this.selectedCountries.map(obj => obj.cc)
		}

		let headers = new HttpHeaders({
			'Authorization': 'Bearer ' + this.us.user?.token?.value,
		});

		// console.log(`\nSearching with query:`, query)

		this.ms.isLoading = true
		this.lastSearchId = 0 + query.searchId;

		this.allInns = []
		this.displayedResults = []

		this.http
			.post(
				`${environment.apiUrl}${window['urlPrefix']}/api/search`,
				query,
				{ headers, responseType: "text" } as any) // "as any" otherwise Typescript says "Type 'string' is not assignable to type '"json"', WTF
			.subscribe({

				// this.parseResults.bind(this),

				next: this.handleSearchResponse.bind(this),

				error: (err: any) => {

					if (err.message.includes("failure during parsing")) {
						// We are probably in dev, the result is in plain JSON instead of encoded text. I'm calling handleSearchResponse anyway
						// console.log(`Looks like the response is plain JSON instead of encoded text, trying to handle the response anyway`)
						this.handleSearchResponse(err.error.text)
						return
					}

					console.error(`${l}Search error`, err)

					this.ms.isLoading = false;
				}
			})
	}

	async handleSearchResponse(response: string | JSON) {

		const l = `ss.handleSearchResponse() - `

		this.ms.isLoading = false
		this.ms.initialSearchDone = true
		this.ms.display.introText = false

		// console.log(`${l}raw response = `, response)

		let parsedResponse: any;
		try {

			// console.log(`${l}Trying JSON.parse...`)
			parsedResponse = JSON.parse(response as any) // JSON, NODE_ENV==='development" - But still arrives as a string since we specified { responseType: "text" }
		} catch (err) {
			// console.log(`${l}Failed to JSON.parse, trying to decrypt string...`)
			parsedResponse = decrypt(response as string); // encrypted, NODE_ENV==="production"
		}

		/* Logging
		if (parsedResponse.searchId !== this.lastSearchId) {
			console.log(`${l}Search response ${parsedResponse.searchId} is outdated, ignoring...`)
			return
		}

		console.log(`${l}response's searchId (${parsedResponse.searchId}) matches the expected one, displaying those results`);
		*/

		let medSets = parsedResponse.results;

		// console.log(`${l}decrypted medSets = `, medSets)

		if (this.searchTerms.length) {

			// computing MedSets relevancy

			this.searchTerms = this.searchTerms.toLowerCase();


			medSets = medSets.map(medSet => {

				// Need to compute the custom keys before the relevancy (which uses joinedStuff custom keys)
				medSet = this.utils.computeMedicineSetCustomKeys(medSet)

				let relevancy = 0

				if (medSet.inns.startsWith(this.searchTerms)) relevancy += 1000; // Big bonus

				medSet.inns = medSet.inns.replace(new RegExp(`(${this.searchTerms})`, 'ig'), (match, p1, offset, string) => {
					relevancy += 1000 - offset
					return `<em>${match}</em>`
				})

				medSet.relevancy = relevancy

				return medSet
			})
				.filter(m => m.relevancy !== 0) // Why is Mongo sending back totally non-matching results? Whatever
				.sort((a, b) => a.relevancy < b.relevancy ? 1 : -1);

		}

		this.ngZone.run(() => {
			this.displayedResults = medSets
		})

		// Terms and conditions modal
		if (localStorage.getItem("displayAcceptanceModal") === null) {
			await this.ms.getDisclaimerOnce()
			this.ms.toggleModal("acceptance", l)
		} else {
			this.ms.setModal("")
		}

	}

	async getAllInns(): Promise<void> {

		if (this.ms.isLoading || this.allInns.length) return;

		this.ms.isLoading = true
		this.ngZone.run(() => {
			this.allInns = []
			this.displayedResults = []
		})

		try {

			const cc = this.selectedCountries.map(obj => obj.cc);
			const response = await lastValueFrom(this.http.post(`${environment.apiUrl}${window['urlPrefix']}/api/getAllInns`, { cc })) as any;

			let inns = response;
			if (typeof inns === "string") {
				inns = JSON.parse(inns);
			}

			if (!Array.isArray(inns)) {
				throw new Error("Invalid /getAllInns response payload");
			}

			this.ngZone.run(() => {
				this.allInns = inns.sort()
				this.ms["display"]["introText"] = false
			})

		} catch (err) {
			console.error(err)
			if (err.status === 401) return this.ms.navigate('/')
		} finally {
			this.ms.isLoading = false
		}
	}
}